import { useState, useEffect } from 'react';
import { NODE_DETAILS, LAYER_COLORS, DOMAINS, type DomainId } from './archData';
import { getDomainComponentIds } from './layout';
import { addToQueue, cancelQueueItem, getNodeQueueItem, type CopilotQueueItem } from './archQueue';

interface DetailPanelProps {
  nodeId: string | null;
  onClose: () => void;
}

const STATUS_CONFIG = {
  pending: { color: '#f59e0b', bg: 'rgba(120,53,15,0.4)', label: '⏳ En attente de Copilot' },
  read:    { color: '#38bdf8', bg: 'rgba(12,74,110,0.4)', label: '👀 Copilot est en train de traiter...' },
  done:    { color: '#4ade80', bg: 'rgba(5,46,22,0.4)',   label: '✅ Traité' },
};

const LAYER_LABEL: Record<string, string> = {
  external: 'Service Externe',
  api: 'Route API (Vercel)',
  utils: 'Utilitaire / Context',
  core: 'Composant Central',
  ui: 'Composant UI',
  hooks: 'Hook React',
};

export default function DetailPanel({ nodeId, onClose }: DetailPanelProps) {
  const [question, setQuestion] = useState('');
  const [existingItem, setExistingItem] = useState<CopilotQueueItem | null>(null);

  useEffect(() => {
    if (!nodeId) { setExistingItem(null); return; }
    setExistingItem(getNodeQueueItem(nodeId));
    setQuestion('');
    const handler = () => setExistingItem(getNodeQueueItem(nodeId));
    window.addEventListener('arch_queue_update', handler);
    return () => window.removeEventListener('arch_queue_update', handler);
  }, [nodeId]);

  if (!nodeId) return null;

  const isDomain = nodeId in DOMAINS;
  const domain = isDomain ? DOMAINS[nodeId as DomainId] : null;
  const detail = isDomain ? null : NODE_DETAILS[nodeId];
  if (!isDomain && !detail) return null;

  const label = domain?.label ?? detail!.label;
  const role = domain?.description ?? detail!.role;
  const connectedServices = detail?.connectedServices ?? [];
  const keyFiles = detail?.keyFiles ?? [];
  const colors = domain ?? LAYER_COLORS[detail!.layer];
  const layerBadge = domain ? 'Domaine' : LAYER_LABEL[detail!.layer];
  const includedLabels = isDomain ? getDomainComponentIds(nodeId as DomainId).map(id => NODE_DETAILS[id].label) : [];

  const handleSend = () => {
    if (!question.trim()) return;
    addToQueue({
      nodeId,
      nodeLabel: label,
      nodeLayer: domain ? 'domain' : detail!.layer,
      nodeRole: role,
      keyFiles,
      question: question.trim(),
    });
    setQuestion('');
  };

  return (
    <div style={{
      position: 'absolute',
      top: 16,
      right: 16,
      width: 320,
      background: '#1e293b',
      border: `2px solid ${colors.border}`,
      borderRadius: 12,
      padding: 20,
      zIndex: 100,
      boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
      fontFamily: 'system-ui, sans-serif',
      color: '#f1f5f9',
      maxHeight: 'calc(100vh - 120px)',
      overflowY: 'auto',
    }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
        <div>
          <span style={{
            display: 'inline-block',
            background: colors.bg,
            color: colors.text,
            fontSize: 11,
            fontWeight: 700,
            padding: '2px 8px',
            borderRadius: 4,
            marginBottom: 6,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
          }}>
            {layerBadge}
          </span>
          <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#f1f5f9' }}>
            {label}
          </h3>
        </div>
        <button
          onClick={onClose}
          style={{ background: 'none', border: 'none', color: '#94a3b8', fontSize: 18, cursor: 'pointer', padding: '0 4px', lineHeight: 1 }}
          aria-label="Fermer"
        >✕</button>
      </div>

      {/* Rôle */}
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', marginBottom: 4 }}>Rôle</div>
        <p style={{ margin: 0, fontSize: 13, lineHeight: 1.6, color: '#cbd5e1' }}>{role}</p>
      </div>

      {/* Éléments inclus (mode domaine uniquement) */}
      {isDomain && includedLabels.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', marginBottom: 6 }}>Ce que ça regroupe</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {includedLabels.map(l => (
              <span key={l} style={{ background: '#334155', color: '#e2e8f0', fontSize: 12, padding: '2px 8px', borderRadius: 4 }}>
                {l}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Services connectés */}
      {connectedServices.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', marginBottom: 6 }}>Services connectés</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {connectedServices.map(svc => (
              <span key={svc} style={{ background: '#334155', color: '#e2e8f0', fontSize: 12, padding: '2px 8px', borderRadius: 4 }}>
                {svc}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Fichiers clés */}
      {keyFiles.length > 0 && (
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', marginBottom: 6 }}>Fichiers clés</div>
          <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
            {keyFiles.map(file => (
              <li key={file} style={{ fontSize: 12, color: '#7dd3fc', fontFamily: 'monospace', padding: '2px 0' }}>
                📄 {file}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Séparateur */}
      <div style={{ borderTop: '1px solid #334155', marginBottom: 16 }} />

      {/* Zone Copilot */}
      <div>
        <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', marginBottom: 10 }}>
          💬 Demander à Copilot
        </div>

        {existingItem && existingItem.status !== 'done' ? (
          /* Message actif pour ce nœud */
          <div style={{
            background: STATUS_CONFIG[existingItem.status].bg,
            border: `1px solid ${STATUS_CONFIG[existingItem.status].color}`,
            borderRadius: 8,
            padding: 12,
          }}>
            <div style={{ fontSize: 12, color: STATUS_CONFIG[existingItem.status].color, fontWeight: 700, marginBottom: 6 }}>
              {STATUS_CONFIG[existingItem.status].label}
            </div>
            <p style={{ margin: '0 0 8px', fontSize: 13, color: '#cbd5e1', fontStyle: 'italic' }}>
              "{existingItem.question}"
            </p>
            <div style={{ fontSize: 11, color: '#64748b', marginBottom: existingItem.status === 'pending' ? 8 : 0 }}>
              {new Date(existingItem.timestamp).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
            </div>
            {existingItem.status === 'pending' && (
              <button
                onClick={() => cancelQueueItem(existingItem.id)}
                style={{
                  background: 'none',
                  border: '1px solid #475569',
                  color: '#94a3b8',
                  borderRadius: 6,
                  padding: '4px 10px',
                  fontSize: 12,
                  cursor: 'pointer',
                }}
              >
                Annuler
              </button>
            )}
          </div>
        ) : (
          /* Formulaire d'envoi */
          <div>
            <textarea
              value={question}
              onChange={e => setQuestion(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) handleSend(); }}
              placeholder={isDomain ? "Décris ce que tu veux faire ou changer dans ce domaine, pas besoin de connaître les fichiers..." : "Décris ta question ou ton problème sur cet élément..."}
              style={{
                width: '100%',
                height: 80,
                background: '#0f172a',
                border: '1px solid #334155',
                borderRadius: 8,
                color: '#f1f5f9',
                fontSize: 13,
                padding: '8px 10px',
                resize: 'none',
                fontFamily: 'system-ui, sans-serif',
                boxSizing: 'border-box',
                outline: 'none',
              }}
            />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
              <span style={{ fontSize: 11, color: '#475569' }}>Ctrl+↵ pour envoyer</span>
              <button
                onClick={handleSend}
                disabled={!question.trim()}
                style={{
                  background: question.trim() ? '#6366f1' : '#1e293b',
                  border: `1px solid ${question.trim() ? '#4f46e5' : '#334155'}`,
                  color: question.trim() ? '#fff' : '#475569',
                  borderRadius: 8,
                  padding: '6px 16px',
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: question.trim() ? 'pointer' : 'default',
                }}
              >
                Envoyer à Copilot →
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
